import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
} from "@mui/material";

const LEAVE_TYPES = [
  { name: "Casual Leave", code: "CL" },
  { name: "Sick Leave", code: "SL" },
  { name: "Earned Leave", code: "EL" },
  { name: "Work From Home", code: "WFH" },
  { name: "Loss Of Pay", code: "LOP" },
];

export default function AddLeaveType() {
  const [form, setForm] = useState({
    company_id: "",
    name: "",
    code: "",
    is_paid: true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleLeaveTypeChange = (name) => {
    const match = LEAVE_TYPES.find((t) => t.name === name);
    setForm((f) => ({ ...f, name, code: match ? match.code : "" }));
    setErrors((e) => ({ ...e, name: undefined, code: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_id.trim()) e.company_id = "Enter a company id";
    if (!form.name) e.name = "Select a leave type";
    if (!form.code.trim()) e.code = "Code is required";
    else if (form.code.length > 20) e.code = "Max 20 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setToast(null);
    try {
      const response = await fetch("/api/leave-types", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          company_id: Number(form.company_id),
          name: form.name,
          code: form.code,
          is_paid: form.is_paid,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          const fieldErrors = {};
          Object.keys(result.errors).forEach((key) => {
            fieldErrors[key] = result.errors[key][0];
          });
          setErrors(fieldErrors);
        }
        setToast({
          type: "error",
          message: result.message || "Could not save leave type.",
        });
        return;
      }

      setToast({ type: "success", message: result.message || "Leave type created successfully." });
      setForm({ company_id: "", name: "", code: "", is_paid: true });
    } catch (err) {
      setToast({ type: "error", message: "Network error. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({ company_id: "", name: "", code: "", is_paid: true });
    setErrors({});
    setToast(null);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f1f5f9", display: "flex", justifyContent: "center", p: 4 }}>
      <Paper elevation={1} sx={{ width: "100%", maxWidth: 420, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ bgcolor: "#1f6fb2", px: 3, py: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, letterSpacing: 0.5 }}>
            ADD LEAVE TYPE
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Company Id"
            required
            fullWidth
            size="small"
            placeholder="e.g. 1"
            value={form.company_id}
            onChange={(e) => update("company_id", e.target.value)}
            error={!!errors.company_id}
            helperText={errors.company_id}
          />

          <TextField
            select
            label="Leave Type"
            required
            fullWidth
            size="small"
            value={form.name}
            onChange={(e) => handleLeaveTypeChange(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          >
            <MenuItem value="">
              <em>Select leave type</em>
            </MenuItem>
            {LEAVE_TYPES.map((t) => (
              <MenuItem key={t.code} value={t.name}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Code"
            fullWidth
            size="small"
            value={form.code}
            InputProps={{ readOnly: true }}
            placeholder="Auto-filled on leave type selection"
            helperText="Set automatically based on the selected leave type."
          />

          <TextField
            select
            label="Is Paid"
            required
            fullWidth
            size="small"
            value={form.is_paid ? "true" : "false"}
            onChange={(e) => update("is_paid", e.target.value === "true")}
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </TextField>

          {toast && <Alert severity={toast.type}>{toast.message}</Alert>}
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, bgcolor: "#f8fafc" }}>
          <Button variant="outlined" color="inherit" onClick={handleReset}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ bgcolor: "#7b3fd4", "&:hover": { bgcolor: "#6b32bf" } }}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}